import Toggle from 'react-toggle';
import type { FunctionComponent } from 'react';

import { withTargetChecked } from '../../../../utils';
import { Link } from '../../../generic';
import FormGroup from '../../../generic/Forms/FormGroup';
import FormLabel from '../../../generic/Forms/FormLabel';
import LabeledTextInput from '../../../generic/Forms/LabeledTextInput';
import MultiTextInput from '../../../generic/Forms/MultiTextInput';
import Select from '../../../generic/Select/Select';
import type { JiraResponse } from '../../../../../shared/jira';
import type { SelectOption } from '../../../../types';
import type { TextInputOnChange } from '../../../generic/Forms/InputBase';

import JiraSettingsSection from './JiraSettingsSection';

//  URLs for each of the "Learn more..." links to Jira documentation.
const JIRA_DOC_LINKS = Object.freeze({
  projectKeys: 'https://confluence.atlassian.com/adminjiraserver071/defining-a-project-802592364.html',
  issueTypes: 'https://support.atlassian.com/jira-cloud-administration/docs/what-are-issue-types/',
  labels: 'https://confluence.atlassian.com/jirasoftwareserver/editing-and-collaborating-on-issues-939938928.html',
});

export type JiraSettingsConfigurationProps = Pick<
  JiraResponse,
  'name' | 'base_url' | 'jiraProjectIds' | 'defaultLicensingProject' |
  'defaultSecurityProject' | 'defaultQualityProject' | 'defaultUniqueTickets' |
  'issueTypes' | 'labels'
> & {
  onNameChange: TextInputOnChange;
  onBaseURLChange: TextInputOnChange;
  onProjectIdsChange: (projectIds: string[]) => void,
  onDefaultLicensingProjectChange: (proj: string | null) => void,
  onDefaultSecurityProjectChange: (proj: string | null) => void,
  onDefaultQualityProjectChange: (proj: string | null) => void,
  onDefaultUniqueTicketsChange: (uniqueTickets: boolean) => void,
  onIssueTypesChange: (issueTypes: string[]) => void,
  onLabelsChange: (labels: string[]) => void,
};

/**
 * The congfiguration form for JiraSettings
 */
const JiraSettingsConfiguration: FunctionComponent<JiraSettingsConfigurationProps> = ({
  name,
  onNameChange,
  base_url: baseURL,
  onBaseURLChange,
  jiraProjectIds: projectIds,
  onProjectIdsChange,
  defaultLicensingProject,
  onDefaultLicensingProjectChange,
  defaultSecurityProject,
  onDefaultSecurityProjectChange,
  defaultQualityProject,
  onDefaultQualityProjectChange,
  defaultUniqueTickets,
  onDefaultUniqueTicketsChange,
  issueTypes,
  onIssueTypesChange,
  labels,
  onLabelsChange,
}) => {
  const onLicensingProjectChange = (o: SelectOption | null) => {
    onDefaultLicensingProjectChange(o?.value || null);
  };

  const onSecurityProjectChange = (o: SelectOption | null) => {
    onDefaultSecurityProjectChange(o?.value || null);
  };

  const onQualityProjectChange = (o: SelectOption | null) => {
    onDefaultQualityProjectChange(o?.value || null);
  };

  return (
    <JiraSettingsSection heading="Configuration" subHeading="Site settings for this Jira instance.">
      <FormGroup>
        <LabeledTextInput
          labelVariant="legacy"
          label="Name"
          placeholder="Add a unique name for this Jira instance"
          onChange={onNameChange}
          value={name}
        />
        <LabeledTextInput
          labelVariant="legacy"
          label="Site URL"
          placeholder="https://your-org.atlassian.com/"
          onChange={onBaseURLChange}
          value={baseURL || ''}
        />
        <FormGroup direction="row">
          <FormLabel
            variant="legacy"
            label="Project Keys"
            htmlFor="projectIds"
            requiredText={
              <Link to={JIRA_DOC_LINKS.projectKeys} openInNewTab>
                (Learn More...)
              </Link>
            }
          >
            <MultiTextInput
              id="projectIds"
              label="Project Keys"
              placeholder="Add project keys..."
              onChange={onProjectIdsChange}
              values={projectIds || []}
            />
          </FormLabel>
        </FormGroup>
        <FormGroup direction="row">
          <FormLabel variant="legacy" label="Default Licensing Project Key" htmlFor="licensing-projectId">
            <Select
              id="licensing-projectId"
              value={
                defaultLicensingProject ? { value: defaultLicensingProject, label: defaultLicensingProject } : null
              }
              onChange={onLicensingProjectChange}
              options={(projectIds || []).map((proj) => ({
                value: proj,
                label: proj,
              }))}
            />
          </FormLabel>
          <FormLabel variant="legacy" label="Default Security Project Key" htmlFor="security-projectId">
            <Select
              id="security-projectId"
              value={
                defaultSecurityProject ? { value: defaultSecurityProject, label: defaultSecurityProject } : null
              }
              onChange={onSecurityProjectChange}
              options={(projectIds || []).map((proj) => ({
                value: proj,
                label: proj,
              }))}
            />
          </FormLabel>
          <FormLabel variant="legacy" label="Default Quality Project Key" htmlFor="quality-projectId">
            <Select
              id="quality-projectId"
              value={
                defaultQualityProject ? { value: defaultQualityProject, label: defaultQualityProject } : null
              }
              onChange={onQualityProjectChange}
              options={(projectIds || []).map((proj) => ({
                value: proj,
                label: proj,
              }))}
            />
          </FormLabel>
        </FormGroup>
        <FormLabel variant="legacy" label="Enable unique Jira tickets by default" htmlFor="default-unique-tickets">
          <Toggle
            id="enabled"
            checked={defaultUniqueTickets}
            onChange={withTargetChecked(onDefaultUniqueTicketsChange)}
            icons={false}
          />
        </FormLabel>
        <FormLabel
          variant="legacy"
          label="Issue Types"
          htmlFor="issueTypes"
          requiredText={
            <Link to={JIRA_DOC_LINKS.issueTypes} openInNewTab>
              (Learn More...)
            </Link>
          }
        >
          <MultiTextInput
            id="issueTypes"
            label="Issue Types"
            placeholder="Add issue types..."
            onChange={onIssueTypesChange}
            values={issueTypes || []}
          />
        </FormLabel>
        <FormLabel
          variant="legacy"
          label="Labels"
          htmlFor="labels"
          requiredText={
            <Link to={JIRA_DOC_LINKS.labels} openInNewTab>
              (Learn More...)
            </Link>
          }
        >
          <MultiTextInput
            id="labels"
            label="Labels"
            placeholder="Add labels..."
            onChange={onLabelsChange}
            values={labels || []}
          />
        </FormLabel>
      </FormGroup>
    </JiraSettingsSection>
  );
};

export default JiraSettingsConfiguration;
